import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { racetrack as racetrackApi } from '../../../utils/api';
import { COUNTRIES } from '../../../constants/countries';
import { COUNTRY_TO_CODE } from '../../../utils/countryUtils';
import ReactCountryFlag from 'react-country-flag';

const CreateRacetrackModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation(['racetracks', 'common']);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    length: '',
    country: '',
    location: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'length' ? parseFloat(value) || '' : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validate form data
      if (!formData.name || !formData.length || !formData.country || !formData.location) {
        toast.error(t('common:validation.allFieldsRequired'));
        return;
      }
      
      await racetrackApi.create(formData);
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error creating racetrack:', error);
      toast.error(t('errors.creatingRacetrack'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      length: '',
      country: '',
      location: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('createRacetrack.title')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('createRacetrack.fields.name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('createRacetrack.fields.length')}
                name="length"
                type="number"
                value={formData.length}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
                required
                inputProps={{ min: 0.1, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>{t('createRacetrack.fields.country')}</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  label={t('createRacetrack.fields.country')}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {COUNTRY_TO_CODE[selected] && (
                        <ReactCountryFlag 
                          countryCode={COUNTRY_TO_CODE[selected]} 
                          svg 
                          style={{ 
                            width: '1.5em', 
                            height: '1.5em',
                            marginRight: '8px',
                            borderRadius: '2px'
                          }}
                        />
                      )}
                      {selected}
                    </Box>
                  )}
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country} value={country}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {COUNTRY_TO_CODE[country] && (
                          <ReactCountryFlag 
                            countryCode={COUNTRY_TO_CODE[country]} 
                            svg 
                            style={{ 
                              width: '1.5em', 
                              height: '1.5em',
                              marginRight: '8px',
                              borderRadius: '2px'
                            }}
                          />
                        )}
                        {country}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('createRacetrack.fields.location')}
                name="location"
                value={formData.location}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateRacetrackModal; 